import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Upload, 
  Download, 
  DollarSign, 
  TrendingUp, 
  Eye, 
  Calendar,
  Clock,
  Edit3,
  BarChart3,
  Music,
  Filter,
  Search,
  Grid3X3,
  List,
  Plus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useUserSequences, UserSequence } from '@/hooks/useUserSequences';
import { useAuth } from '@/contexts/AuthContext';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export const MySequencesContent = () => {
  const { sequences, analytics, loading, error } = useUserSequences();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'title' | 'downloads' | 'revenue'>('date');
  const [filterBy, setFilterBy] = useState<'all' | 'free' | 'paid' | 'pending' | 'approved'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Filter and search sequences
  const filteredSequences = sequences.filter(seq => {
    const matchesSearch = !searchQuery || 
      seq.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      seq.artist_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filterBy === 'all' || 
      (filterBy === 'free' && seq.is_free) ||
      (filterBy === 'paid' && !seq.is_free) ||
      (filterBy === 'pending' && seq.status === 'pending') ||
      (filterBy === 'approved' && seq.status === 'approved');

    return matchesSearch && matchesFilter;
  });

  // Sort sequences
  const sortedSequences = [...filteredSequences].sort((a, b) => {
    switch (sortBy) {
      case 'title':
        return a.title.localeCompare(b.title);
      case 'downloads':
        return b.total_downloads - a.total_downloads;
      case 'revenue':
        return b.total_revenue - a.total_revenue;
      case 'date':
      default:
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    }
  });

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">Sign In Required</h1>
          <p className="text-muted-foreground mb-8">Please sign in to view your sequences.</p>
          <Button asChild>
            <Link to="/auth">Sign In</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">My Sequences</h1>
          <p className="text-muted-foreground">
            Manage your uploaded sequences, view analytics, and track performance.
          </p>
        </div>
        <Button asChild>
          <Link to="/upload-sequence">
            <Plus className="h-4 w-4 mr-2" />
            Upload New Sequence
          </Link>
        </Button>
      </div>

      {/* Analytics Dashboard */}
      <Tabs defaultValue="overview" className="mb-8">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Sequences</CardTitle>
                <Upload className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analytics?.total_sequences || 0}</div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Downloads</CardTitle>
                <Download className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analytics?.total_downloads || 0}</div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${analytics?.total_revenue.toFixed(2) || '0.00'}</div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Display Usage</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {analytics?.display_usage_by_year.reduce((sum, item) => sum + item.displays, 0) || 0}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Popular Sequences */}
          {analytics?.popular_sequences && analytics.popular_sequences.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2" />
                  Most Popular Sequences
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {analytics.popular_sequences.map((seq, index) => (
                    <div key={seq.id} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium">{seq.title}</p>
                          <p className="text-sm text-muted-foreground">{seq.downloads} downloads</p>
                        </div>
                      </div>
                      <Button size="sm" variant="outline" asChild>
                        <Link to={`/sequence/${seq.id}`}>
                          <Eye className="h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Downloads Over Time */}
            <Card>
              <CardHeader>
                <CardTitle>Downloads Over Time</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={analytics?.downloads_by_year}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="downloads" stroke="#8884d8" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Display Usage by Year */}
            <Card>
              <CardHeader>
                <CardTitle>Display Usage by Year</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analytics?.display_usage_by_year}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="displays" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Downloads by Month */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Downloads by Month (Last 12 Months)</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={analytics?.downloads_by_month}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(value) => [`${value}`, 'Downloads']} />
                    <Line type="monotone" dataKey="downloads" stroke="#10b981" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Revenue by Month */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Revenue by Month ({new Date().getFullYear()})</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analytics?.revenue_by_month}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(value) => [`$${value}`, 'Revenue']} />
                    <Bar dataKey="revenue" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search sequences or artists..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="date">Upload Date</SelectItem>
            <SelectItem value="title">Title</SelectItem>
            <SelectItem value="downloads">Downloads</SelectItem>
            <SelectItem value="revenue">Revenue</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={filterBy} onValueChange={(value: any) => setFilterBy(value)}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sequences</SelectItem>
            <SelectItem value="free">Free Only</SelectItem>
            <SelectItem value="paid">Paid Only</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex border rounded-md">
          <Button
            variant={viewMode === 'grid' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('grid')}
            className="rounded-r-none"
          >
            <Grid3X3 className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('list')}
            className="rounded-l-none"
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Sequences List/Grid */}
      {loading ? (
        <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <Skeleton className="h-16 w-16 rounded" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-[250px]" />
                    <Skeleton className="h-4 w-[200px]" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-destructive">Error loading sequences: {error}</p>
          </CardContent>
        </Card>
      ) : sortedSequences.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Sequences Yet</h3>
            <p className="text-muted-foreground mb-6">
              {searchQuery || filterBy !== 'all' 
                ? 'No sequences match your search criteria.'
                : 'Upload your first light sequence to get started!'
              }
            </p>
            <Button asChild>
              <Link to="/upload-sequence">Upload Your First Sequence</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className={viewMode === 'grid' 
          ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' 
          : 'space-y-4'
        }>
          {sortedSequences.map((sequence) => (
            <SequenceCard 
              key={sequence.id} 
              sequence={sequence} 
              viewMode={viewMode}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Individual sequence card component
const SequenceCard = ({ 
  sequence, 
  viewMode 
}: { 
  sequence: UserSequence;
  viewMode: 'grid' | 'list';
}) => {
  if (viewMode === 'list') {
    return (
      <Card className="hover:shadow-md transition-shadow">
        <CardContent className="p-6">
          <div className="flex items-center space-x-4">
            {/* Sequence Image */}
            <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center overflow-hidden">
              {(sequence.image_url || sequence.album_cover_url) ? (
                <img 
                  src={sequence.image_url || sequence.album_cover_url} 
                  alt={sequence.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Music className="h-6 w-6 text-muted-foreground" />
              )}
            </div>

            {/* Sequence Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <Link 
                    to={`/sequence/${sequence.id}`}
                    className="font-semibold text-lg hover:text-primary transition-colors truncate block"
                  >
                    {sequence.title}
                  </Link>
                  {sequence.artist_name && (
                    <p className="text-muted-foreground">by {sequence.artist_name}</p>
                  )}
                  <div className="flex items-center space-x-4 mt-2 text-sm text-muted-foreground">
                    <div className="flex items-center">
                      <Download className="h-4 w-4 mr-1" />
                      {sequence.total_downloads}
                    </div>
                    <div className="flex items-center">
                      <DollarSign className="h-4 w-4 mr-1" />
                      {sequence.total_revenue.toFixed(2)}
                    </div>
                    <Badge variant={
                      sequence.status === 'approved' ? 'default' : 
                      sequence.status === 'pending' ? 'secondary' : 'destructive'
                    }>
                      {sequence.status}
                    </Badge>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2 ml-4">
                  <Button size="sm" variant="outline" asChild>
                    <Link to={`/my-sequences/edit/${sequence.id}`}>
                      <Edit3 className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button size="sm" variant="outline" asChild>
                    <Link to={`/sequence/${sequence.id}`}>
                      <Eye className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        {/* Sequence Image */}
        <div className="w-full h-48 rounded-lg bg-muted flex items-center justify-center overflow-hidden mb-4">
          {(sequence.image_url || sequence.album_cover_url) ? (
            <img 
              src={sequence.image_url || sequence.album_cover_url} 
              alt={sequence.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <Music className="h-12 w-12 text-muted-foreground" />
          )}
        </div>

        {/* Sequence Info */}
        <div className="space-y-3">
          <div>
            <Link 
              to={`/sequence/${sequence.id}`}
              className="font-semibold text-lg hover:text-primary transition-colors line-clamp-2"
            >
              {sequence.title}
            </Link>
            {sequence.artist_name && (
              <p className="text-muted-foreground text-sm">by {sequence.artist_name}</p>
            )}
          </div>

          {/* Stats */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-3 text-muted-foreground">
              <div className="flex items-center">
                <Download className="h-4 w-4 mr-1" />
                {sequence.total_downloads}
              </div>
              <div className="flex items-center">
                <DollarSign className="h-4 w-4 mr-1" />
                {sequence.total_revenue.toFixed(2)}
              </div>
            </div>
            <Badge variant={
              sequence.status === 'approved' ? 'default' : 
              sequence.status === 'pending' ? 'secondary' : 'destructive'
            }>
              {sequence.status}
            </Badge>
          </div>

          {/* Metadata */}
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center">
              <Calendar className="h-3 w-3 mr-1" />
              {new Date(sequence.created_at).toLocaleDateString()}
            </div>
            {sequence.duration && (
              <div className="flex items-center">
                <Clock className="h-3 w-3 mr-1" />
                {Math.floor(sequence.duration / 60)}:{(sequence.duration % 60).toString().padStart(2, '0')}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2 pt-3">
            <Button size="sm" variant="outline" className="flex-1" asChild>
              <Link to={`/my-sequences/edit/${sequence.id}`}>
                <Edit3 className="h-4 w-4 mr-2" />
                Edit
              </Link>
            </Button>
            <Button size="sm" variant="outline" className="flex-1" asChild>
              <Link to={`/sequence/${sequence.id}`}>
                <Eye className="h-4 w-4 mr-2" />
                View
              </Link>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};